import { useState, useEffect } from 'react';
import logger from '../../lib/logger';
import AdminLayout from '../../components/layout/AdminLayout';
import { Search, RefreshCw, ChevronDown, ChevronUp, AlertCircle, CheckCircle, Clock, XCircle, FileText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const MercadoPagoLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [availableStatuses, setAvailableStatuses] = useState([]);
  const [expandedRows, setExpandedRows] = useState(new Set());

  const fetchLogs = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/admin/mercadopago-logs?limit=100`;
      if (statusFilter && statusFilter !== 'all') {
        url += `&status=${statusFilter}`;
      }
      if (searchTerm) {
        url += `&search=${encodeURIComponent(searchTerm)}`;
      }

      const response = await fetch(url, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Erro ao buscar logs');
      }

      const data = await response.json();
      setLogs(data.logs || []);
      setAvailableStatuses(data.available_statuses || []);
    } catch (error) {
      logger.error('Error fetching logs:', error);
      toast.error('Erro ao carregar logs do Mercado Pago');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [statusFilter]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchLogs();
  };

  const toggleExpand = (id) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const getStatusBadge = (status) => {
    const config = {
      approved: { 
        icon: CheckCircle, 
        className: 'bg-green-500/20 text-green-400 border-green-500/30',
        label: 'Aprovado'
      },
      pending: { 
        icon: Clock, 
        className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
        label: 'Pendente'
      },
      rejected: { 
        icon: XCircle, 
        className: 'bg-red-500/20 text-red-400 border-red-500/30',
        label: 'Rejeitado'
      },
      cancelled: { 
        icon: XCircle, 
        className: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
        label: 'Cancelado'
      },
      unknown: { 
        icon: AlertCircle, 
        className: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
        label: 'Desconhecido'
      }
    };

    const { icon: Icon, className, label } = config[status] || config.unknown;

    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${className}`}>
        <Icon className="w-3.5 h-3.5" />
        {label}
      </span>
    );
  };

  const getOrigemBadge = (origem) => {
    if (origem === 'teste') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
          TESTE
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400 border border-blue-500/30">
        REAL
      </span>
    );
  };

  if (loading && logs.length === 0) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-kairos-orange border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="mercadopago-logs-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl text-white mb-2">Logs Mercado Pago</h1>
            <p className="text-gray-400">Auditoria de webhooks e transações do Mercado Pago</p>
          </div>
          <Button
            onClick={fetchLogs}
            disabled={loading}
            className="bg-white/10 hover:bg-white/20 text-white"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>

        {/* Filters */}
        <Card className="bg-obsidian-200 border-white/10">
          <CardContent className="p-4">
            <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input
                  placeholder="Buscar por payment_id, preference_id ou referência..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-obsidian border-white/10 text-white placeholder:text-gray-600"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full md:w-48 bg-obsidian border-white/10 text-white">
                  <SelectValue placeholder="Filtrar por status" />
                </SelectTrigger>
                <SelectContent className="bg-obsidian-200 border-white/10">
                  <SelectItem value="all" className="text-white hover:bg-white/10">Todos os status</SelectItem>
                  {availableStatuses.map(status => (
                    <SelectItem key={status} value={status} className="text-white hover:bg-white/10">
                      {status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button type="submit" className="bg-kairos-orange hover:bg-kairos-orange-dark text-white">
                Buscar
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-obsidian-200 border-white/10">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-white">{logs.length}</p>
              <p className="text-xs text-gray-500">Total de Logs</p>
            </CardContent>
          </Card>
          <Card className="bg-obsidian-200 border-green-500/30">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-green-400">
                {logs.filter(l => l.status === 'approved').length}
              </p>
              <p className="text-xs text-gray-500">Aprovados</p>
            </CardContent>
          </Card>
          <Card className="bg-obsidian-200 border-yellow-500/30">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-yellow-400">
                {logs.filter(l => l.status === 'pending').length}
              </p>
              <p className="text-xs text-gray-500">Pendentes</p>
            </CardContent>
          </Card>
          <Card className="bg-obsidian-200 border-red-500/30">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-red-400">
                {logs.filter(l => l.status === 'rejected' || l.status === 'cancelled').length}
              </p>
              <p className="text-xs text-gray-500">Rejeitados</p>
            </CardContent>
          </Card>
        </div>

        {/* Logs Table */}
        <Card className="bg-obsidian-200 border-white/10">
          <CardHeader>
            <CardTitle className="text-white font-display flex items-center gap-2">
              <FileText className="w-5 h-5 text-kairos-orange" />
              Registros ({logs.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {logs.length === 0 ? (
              <div className="text-center py-12">
                <AlertCircle className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-500">Nenhum log encontrado</p>
                <p className="text-gray-600 text-sm mt-1">
                  Os logs aparecerão aqui quando o webhook do Mercado Pago disparar.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="table-dark w-full">
                  <thead>
                    <tr>
                      <th className="text-left">Data/Hora</th>
                      <th className="text-left">Payment ID</th>
                      <th className="text-left">Preference ID</th>
                      <th className="text-center">Status</th>
                      <th className="text-right">Valor</th>
                      <th className="text-center">Origem</th>
                      <th className="text-center">Raw</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log, index) => (
                      <>
                        <tr key={log.id || index} className="hover:bg-white/5">
                          <td className="text-gray-300 text-sm whitespace-nowrap">
                            {formatDate(log.created_at)}
                          </td>
                          <td className="text-white font-mono text-sm">
                            {log.payment_id || '-'}
                          </td>
                          <td className="text-gray-400 font-mono text-sm truncate max-w-[200px]" title={log.preference_id}>
                            {log.preference_id ? `${log.preference_id.substring(0, 20)}...` : '-'}
                          </td>
                          <td className="text-center">
                            {getStatusBadge(log.status)}
                          </td>
                          <td className="text-right text-white">
                            {formatCurrency(log.amount)}
                          </td>
                          <td className="text-center">
                            {getOrigemBadge(log.endpoint_origem)}
                          </td>
                          <td className="text-center">
                            <button
                              onClick={() => toggleExpand(log.id || index)}
                              className="p-1.5 rounded hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
                              title="Ver dados completos"
                            >
                              {expandedRows.has(log.id || index) ? (
                                <ChevronUp className="w-4 h-4" />
                              ) : (
                                <ChevronDown className="w-4 h-4" />
                              )}
                            </button>
                          </td>
                        </tr>
                        {expandedRows.has(log.id || index) && (
                          <tr key={`${log.id || index}-expanded`}>
                            <td colSpan={7} className="bg-obsidian p-4">
                              <div className="rounded-lg bg-black/30 p-4 overflow-x-auto">
                                <p className="text-xs text-gray-500 mb-2 font-medium">RAW EVENT:</p>
                                <pre className="text-xs text-gray-300 whitespace-pre-wrap font-mono">
                                  {JSON.stringify(log.raw_event, null, 2)}
                                </pre>
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info */}
        <div className="text-center text-gray-500 text-sm">
          <p>Esta página exibe os últimos 100 registros de webhooks e transações do Mercado Pago.</p>
          <p>Use para verificar se os webhooks estão sendo recebidos e processados corretamente.</p>
        </div>
      </div>
    </AdminLayout>
  );
};

export default MercadoPagoLogsPage;
